// Browser-side marker occlusion via a precomputed voxel-occupancy grid.
//
// The conversion service bakes a coarse occupancy grid per point cloud
// (occupancy.bin, see tools/occupancy_grid.py). Smart View fetches every file's
// grid, unions them, and raycasts marker line-of-sight against the union — so a
// tour/note/measurement marker is hidden when point-cloud geometry sits between
// the viewer's station and the marker. This is instant, smooth, cross-file and
// load-independent, replacing the noisy live GPU-pick occlusion.

export interface OccGrid { voxel: number; coords: Int32Array; }   // absolute world voxel coords (x,y,z each)
// The union of every file's grid as bit blocks of 32³ voxels (4 KB each) keyed
// by block. It used to be one Set of packed voxel numbers rebuilt from every
// grid each time a file's grid arrived: 100 files of 100k voxels cost 93 s of
// main thread and ~2 GB of heap, and the packing (17 bits an axis from the
// lowest voxel) overflowed once one cloud sat hundreds of km from the rest.
// `last*` caches the block of the previous lookup: rays walk voxel by voxel.
export interface OccIndex { voxel: number; blocks: Map<number, Uint32Array>; minz: number; lastKey: number; lastBlock: Uint32Array | undefined; }
interface V3 { x: number; y: number; z: number; }

const SKIP = 0.5;           // m — ignore geometry within this of either endpoint (the scanner's own artefacts).
                            // Small on purpose: a wall in the next room is often ~1–2 m from a station, so a
                            // large skip would step right over it (the "visible through the wall" bug).
const SKIP_FRAC = 0.4;      // …but never skip more than this fraction of a short ray, or nothing gets checked.
const OCC_MIN = 2;          // distinct occupied voxels between endpoints ⇒ occluded. Eye-level rays pass over low
                            // furniture, so a hit here is a wall/column/counter; 2 rejects a lone stray voxel.

// Block coordinates in range: |x|, |y| < 2^20 blocks (10,000 km at 0.3 m), |z| < 2^10 blocks (9.8 km).
const XY_BIAS = 1 << 20, Z_BIAS = 1 << 10;
const blockKey = (bi: number, bj: number, bk: number) =>
  ((bi + XY_BIAS) * 2 ** 21 + (bj + XY_BIAS)) * 2 ** 11 + (bk + Z_BIAS);
const inRange = (bi: number, bj: number, bk: number) =>
  bi >= -XY_BIAS && bi < XY_BIAS && bj >= -XY_BIAS && bj < XY_BIAS && bk >= -Z_BIAS && bk < Z_BIAS;

/** Whether world voxel (i, j, k) is occupied. */
export function occHas(ix: OccIndex, i: number, j: number, k: number): boolean {
  const bi = i >> 5, bj = j >> 5, bk = k >> 5;
  if (!inRange(bi, bj, bk)) return false;
  const key = blockKey(bi, bj, bk);
  let block = ix.lastBlock;
  if (key !== ix.lastKey) { block = ix.blocks.get(key); ix.lastKey = key; ix.lastBlock = block; }
  if (!block) return false;
  const bit = ((i & 31) << 10) | ((j & 31) << 5) | (k & 31);
  return (block[bit >>> 5] & (1 << (bit & 31))) !== 0;
}

/** Parse an OCC1 occupancy blob. Returns null if it isn't one (e.g. a 404 body). */
export function parseOccupancy(buf: ArrayBuffer): OccGrid | null {
  if (buf.byteLength < 12) return null;
  const dv = new DataView(buf);
  if (dv.getUint8(0) !== 0x4f || dv.getUint8(1) !== 0x43 || dv.getUint8(2) !== 0x43 || dv.getUint8(3) !== 0x31) return null; // "OCC1"
  const voxel = dv.getFloat32(4, true);
  const count = dv.getUint32(8, true);
  if (!voxel || count * 12 + 12 > buf.byteLength) return null;
  return { voxel, coords: new Int32Array(buf.slice(12, 12 + count * 12)) };
}

/**
 * Adds one file's grid to the union and returns a NEW index object over the
 * same blocks (so React sees a change) — each grid is written once, when it
 * arrives. A grid of another voxel size is skipped: they would not line up.
 */
export function addToOccIndex(ix: OccIndex | null, grid: OccGrid | null): OccIndex | null {
  if (!grid || !grid.coords.length) return ix;
  if (ix && Math.abs(ix.voxel - grid.voxel) > 1e-9) return ix;
  const blocks = ix ? ix.blocks : new Map<number, Uint32Array>();
  let minz = ix ? ix.minz : Infinity;
  const c = grid.coords;
  let lastKey = NaN, block: Uint32Array | undefined;
  for (let n = 0; n < c.length; n += 3) {
    const i = c[n], j = c[n + 1], k = c[n + 2];
    const bi = i >> 5, bj = j >> 5, bk = k >> 5;
    if (!inRange(bi, bj, bk)) continue;
    const key = blockKey(bi, bj, bk);
    if (key !== lastKey) {
      block = blocks.get(key);
      if (!block) { block = new Uint32Array(1024); blocks.set(key, block); }
      lastKey = key;
    }
    const bit = ((i & 31) << 10) | ((j & 31) << 5) | (k & 31);
    block![bit >>> 5] |= 1 << (bit & 31);
    if (k < minz) minz = k;
  }
  if (!blocks.size) return ix;
  return { voxel: grid.voxel, blocks, minz, lastKey: NaN, lastBlock: undefined };
}

/** Union several files' grids into one queryable index (all grids share the voxel size). */
export function buildOccIndex(grids: OccGrid[]): OccIndex | null {
  let ix: OccIndex | null = null;
  for (const g of grids) ix = addToOccIndex(ix, g);
  return ix;
}

/** True if point-cloud geometry occludes the segment a→b (skipping a radius around each end). */
export function isOccluded(ix: OccIndex, a: V3, b: V3): boolean {
  const { voxel } = ix;
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
  const d = Math.hypot(dx, dy, dz);
  const step = voxel * 0.5;
  const n = Math.floor(d / step);
  if (n < 3) return false;
  const skip = Math.min(SKIP, d * SKIP_FRAC);   // clear scanner artefacts, but always check a short ray's middle
  const hit = new Set<string>();
  for (let i = 1; i < n; i++) {
    const t = i / n;
    const px = a.x + dx * t, py = a.y + dy * t, pz = a.z + dz * t;
    if (Math.hypot(px - a.x, py - a.y, pz - a.z) < skip) continue;
    if (Math.hypot(px - b.x, py - b.y, pz - b.z) < skip) continue;
    const vi = Math.floor(px / voxel), vj = Math.floor(py / voxel), vk = Math.floor(pz / voxel);
    if (occHas(ix, vi, vj, vk)) { hit.add(`${vi},${vj},${vk}`); if (hit.size >= OCC_MIN) return true; }
  }
  return false;
}

/**
 * Floor height under a scan station, from the grid. Walking down from the layer
 * under the scanner's own, the first layer that is a continuous surface: present
 * in `solid` of the columns within `radius` that hold anything in reach. The layer
 * right under the scanner must be `underScanner` complete — the tripod, the
 * operator and floor points spilling over a voxel boundary live there, and a short
 * tripod on a 0.3 m grid leaves only that one layer between head and floor.
 * Furniture tops and a raised floor's cable trays never reach `solid`, a hole in
 * the floor stays a minority, and a scan taken from inside a raised floor gets the
 * sub-floor while one standing on the tiles gets the tiles (verified on a data
 * hall: 171 stations, 91 of them under the floor). Null when nothing continuous
 * lies within `maxDrop` (a station at the scan's edge) — the caller falls back to
 * a fixed scanner height.
 */
export function floorBelow(
  ix: OccIndex, pos: V3,
  { radius = 1.5, maxDrop = 3.5, minColumns = 6, solid = 0.75, underScanner = 0.95 } = {},
): number | null {
  const { voxel, minz } = ix;
  const cx = Math.floor(pos.x / voxel), cy = Math.floor(pos.y / voxel);
  const r = Math.ceil(radius / voxel);
  const kTop = Math.floor(pos.z / voxel) - 1;
  const kBot = Math.max(minz, Math.floor((pos.z - maxDrop) / voxel));
  if (kTop < kBot) return null;
  const columns: Array<[number, number]> = [];
  for (let i = cx - r; i <= cx + r; i++) {
    for (let j = cy - r; j <= cy + r; j++) {
      if (Math.hypot((i + 0.5) * voxel - pos.x, (j + 0.5) * voxel - pos.y) > radius) continue;
      for (let k = kBot; k <= kTop; k++) {
        if (occHas(ix, i, j, k)) { columns.push([i, j]); break; }
      }
    }
  }
  if (columns.length < minColumns) return null;
  for (let k = kTop; k >= kBot; k--) {
    let n = 0;
    for (const [i, j] of columns) if (occHas(ix, i, j, k)) n++;
    if (n / columns.length >= (k === kTop ? underScanner : solid)) return (k + 0.5) * voxel;
  }
  return null;
}

/**
 * True when the floor spot of a tour ring is out of sight from a station. A WALL
 * crosses the line from the eye to a point `lift` above the ring: a column
 * occupied from the crossing up to `wallHeight` above the floor of the storey it
 * stands in (the viewer's, or the ring's for a crossing in the ring's storey) AND
 * flanked by two such columns on opposite sides (a wall, a rack row, a cage),
 * outside the target station's own tripod (`own` around the ring). For a ring
 * below the viewer's floor, the viewer's own floor crossed anywhere hides it (a
 * scan from inside a raised floor shows only through a lifted tile). For a ring
 * a STOREY or more away (`storey`), a floor crossed between the two hides it too:
 * a voxel with three of its four horizontal neighbours occupied, under the
 * viewer's floor down to the ring's, or from above the viewer's head up to and
 * including the ring's own floor — so a tower's floors show only through a hatch
 * or a stairwell. What never hides a ring: low clutter, a barrier, a lone pillar,
 * and the ghosts of people — a merged cloud of 171 scans held the workers at
 * hundreds of places, each a 1.8 m blob or a trail of them, which a plain "tall
 * column" test took for walls across open floor. The ring's own floor layer is
 * ignored on its storey. The eye-level test (`isOccluded`) hid nearly every ring:
 * at a short tripod's height a construction site is cluttered everywhere.
 */
export function ringOccluded(
  ix: OccIndex, eye: V3, eyeFloorZ: number, ring: V3, ringFloorZ: number,
  { lift = 0.4, wallHeight = 2.1, own = 0.6, storey = 1.5 } = {},
): boolean {
  const { voxel } = ix;
  const has = (i: number, j: number, k: number) => occHas(ix, i, j, k);
  const b = { x: ring.x, y: ring.y, z: ringFloorZ + lift };
  const eyeK = Math.floor(eyeFloorZ / voxel), ringK = Math.floor(ringFloorZ / voxel);
  const below = ringK < eyeK, above = ringK > eyeK;
  const otherStorey = Math.abs(ringFloorZ - eyeFloorZ) >= storey;
  const wallTop = (z: number) => {
    const base = (below && z < eyeFloorZ) || (above && z >= ringFloorZ) ? ringFloorZ : eyeFloorZ;
    return Math.floor((base + wallHeight) / voxel);
  };
  const tallTo = (i: number, j: number, k: number, top: number) => {
    if (k > top) return false;
    for (let kk = k; kk <= top; kk++) if (!has(i, j, kk)) return false;
    return true;
  };
  const slab = (i: number, j: number, k: number) =>
    (has(i - 1, j, k) ? 1 : 0) + (has(i + 1, j, k) ? 1 : 0)
    + (has(i, j - 1, k) ? 1 : 0) + (has(i, j + 1, k) ? 1 : 0) >= 3;
  const dx = b.x - eye.x, dy = b.y - eye.y, dz = b.z - eye.z;
  const d = Math.hypot(dx, dy, dz);
  const n = Math.floor(d / (voxel * 0.5));
  if (n < 3) return false;
  const skip = Math.min(SKIP, d * SKIP_FRAC);
  for (let s = 1; s < n; s++) {
    const t = s / n;
    const px = eye.x + dx * t, py = eye.y + dy * t, pz = eye.z + dz * t;
    if (Math.hypot(px - eye.x, py - eye.y, pz - eye.z) < skip) continue;
    const ci = Math.floor(px / voxel), cj = Math.floor(py / voxel), ck = Math.floor(pz / voxel);
    if (!has(ci, cj, ck)) continue;
    if (below && ck === eyeK) return true;
    if (otherStorey && slab(ci, cj, ck)) {
      if (below && ck > ringK + 1 && ck < eyeK) return true;
      if (above && ck >= eyeK + 2 && ck <= ringK) return true;
    }
    if (ck === ringK) continue;
    if (Math.hypot(px - ring.x, py - ring.y) < own) continue;
    const top = wallTop(pz);
    if (!tallTo(ci, cj, ck, top)) continue;
    for (const [a, c] of OPPOSITE) {
      if (tallTo(ci + a[0], cj + a[1], ck, top) && tallTo(ci + c[0], cj + c[1], ck, top)) return true;
    }
  }
  return false;
}

const OPPOSITE: Array<[[number, number], [number, number]]> = [
  [[-1, 0], [1, 0]], [[0, -1], [0, 1]], [[-1, -1], [1, 1]], [[-1, 1], [1, -1]],
];
